/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import {
    AzureCsAccountKeysSwagger,
    AzureCsAccountProps,
    AzureCsAccountSwagger,
    AzureCsFeatureInfoSwagger,
    AzureCsResourceProviderInfoSwagger,
    AzurePermissionsSwagger,
    AzureResourceGroupSwagger,
    AzureSearchServiceAdminKeysSwagger,
    AzureSearchServiceNameAvailabilitySwagger,
    AzureSearchServiceSwagger,
    AzureSkuInfo,
    AzureSubscriptionSwagger,
    AzureTenantSwagger,
    RoleAssignmentSwagger
} from 'src/core/services/azure/azureSwaggers';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureResourceGroupStore } from 'src/core/stores/AzureResourceGroupStore';
import { AzureSearchStore } from 'src/core/stores/AzureSearchStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { ResponseResourcesValue } from 'src/core/types/genericApiResponse';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { Region } from 'src/core/types/regionTypes';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, ServiceContext } from 'src/core/utils/createService';
import { getGuid } from 'src/core/utils/randomValueGenerators';

type AzureResponse<T> = { value: T; nextLink?: string };
export type ChunkedAzureData<T> = { data: T[]; error: Error | undefined };
type AzureCsAccountsProviders = 'Microsoft.CognitiveServices' | 'Microsoft.MachineLearningServices';
type AzureServiceParams = Pick<AzureSubscriptionStore, 'id'> & { tenantToken: string; providers?: AzureCsAccountsProviders };

export const pullChunkedAzureData = async <T>(url: string, urlOptions: LuisRequestOptions): Promise<ChunkedAzureData<T>> => {
    const bufferObject: ChunkedAzureData<T> = { data: [], error: undefined };
    let nextLinkToHit = url;

    while (nextLinkToHit) {
        try {
            const { data: resources } = await httpService.get<AzureResponse<readonly T[]>>(nextLinkToHit, urlOptions);
            bufferObject.data.push(...resources.value);
            nextLinkToHit = resources.nextLink;
        } catch (e) {
            if (!bufferObject.data.length) {
                throw e;
            }

            bufferObject.error = <Error>e;
            nextLinkToHit = null;
        }
    }

    return bufferObject;
};

/**
 * Represents a service for fetching Azure resources
 * and ARM tokens.
 */
export const getAzureService = createService(({ getEnvironment }: ServiceContext<object>) => {
    const { azureBaseUrl } = getEnvironment();

    const getRequestOptions = (token: string) =>
        luisRequestOptionsBuilder()
            .useContentType('json')
            .useArmToken(token)
            .skipThrottling()
            .build();

    /**
     * Gets a list of Tenants.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/tenants/list
     *
     * @param param0.neutralToken the default token required to
     * fetch all tenants for user account.
     * @returns A promise with the tenants.
     */
    const getAzureTenants = async ({ neutralToken }: { neutralToken: string }): Promise<ChunkedAzureData<AzureTenantSwagger>> => {
        const urlOptions = getRequestOptions(neutralToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('tenants')
            .queryParam('api-version', '2020-01-01')
            .build();

        return await pullChunkedAzureData<AzureTenantSwagger>(url, urlOptions);
    };

    /**
     * Gets a list of subscriptions.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/subscriptions/list
     *
     * @param param0.tenantToken the tenant token required to fetch all subscriptions
     * under this tenant.
     * @returns A promise with the subscription.
     */
    const getAzureSubscriptionsByTenant = async ({
        tenantToken
    }: Pick<AzureServiceParams, 'tenantToken'>): Promise<ChunkedAzureData<AzureSubscriptionSwagger>> => {
        const urlOptions = getRequestOptions(tenantToken);

        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions')
            .queryParam('api-version', '2020-01-01')
            .build();

        return await pullChunkedAzureData<AzureSubscriptionSwagger>(url, urlOptions);
    };

    /**
     * Gets a list of resource groups.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/resource-groups/list
     *
     * @param param0.id The subscription id required to fetch all
     * resource groups under it.
     * @param param0.tenantToken The tenant token required to fetch all
     * resource groups under it.
     * @returns A promise with the resource groups.
     */
    const getAzureResourceGroupsBySubscriptionId = async ({
        id,
        tenantToken
    }: AzureServiceParams): Promise<ChunkedAzureData<AzureResourceGroupSwagger>> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups')
            .queryParam('api-version', '2021-04-01')
            .build();

        return await pullChunkedAzureData<AzureResourceGroupSwagger>(url, urlOptions);
    };

    /**
     * Gets a list of azure cognitive service account.
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/list-by-resource-group
     *
     * @param param0.id The subscription id to get all resources
     * under it or subset of the resources if filter specified.
     * @param param0.name The name for the new resource group to be created.
     * @param param0.tenantToken The tenant token to get all resources
     * under it or subset of the resources if filter specified.
     * @returns A promise with the list of azure resources.
     */
    const getAzureCsAccountsByResourceGroupName = async ({
        id,
        name,
        tenantToken
    }: AzureServiceParams & Pick<AzureResourceGroupStore, 'name'>): Promise<ChunkedAzureData<AzureCsAccountSwagger>> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', name)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('accounts')
            .queryParam('api-version', '2021-10-01')
            .build();

        return await pullChunkedAzureData<AzureCsAccountSwagger>(url, urlOptions);
    };

    /**
     * Gets a list of azure cognitive service accounts.
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/list
     *
     * @param param0.id The subscription id to get all resources under it
     * or subset of the resources if filter specified.
     * @param param0.tenantToken The tenant token to get all resources under
     * it or subset of the resources if filter specified.
     * @returns A promise with the list of azure resources.
     */
    const getAzureCsAccountsBySubscriptionId = async ({
        id,
        tenantToken
    }: AzureServiceParams): Promise<ChunkedAzureData<AzureCsAccountSwagger>> => {
        const urlOptions = getRequestOptions(tenantToken);

        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('accounts')
            .queryParam('api-version', '2021-10-01')
            .build();

        return await pullChunkedAzureData<AzureCsAccountSwagger>(url, urlOptions);
    };

    /**
     * Gets a list of azure AML cognitive service workspaces.
     *
     * @param param0.id The subscription id to get all resources under it
     * or subset of the resources if filter specified.
     * @param param0.tenantToken The tenant token to get all resources under
     * it or subset of the resources if filter specified.
     * @returns A promise with the list of azure resources.
     */
    const getAzureMLWorkspacesBySubscriptionId = async ({
        id,
        tenantToken
    }: AzureServiceParams): Promise<ChunkedAzureData<AzureCsAccountSwagger>> => {
        const urlOptions = getRequestOptions(tenantToken);

        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.MachineLearningServices')
            .resource('workspaces')
            .queryParam('api-version', '2021-10-01')
            .build();

        return await pullChunkedAzureData<AzureCsAccountSwagger>(url, urlOptions);
    };

    /**
     * Gets a azure AML cognitive service workspace.
     *
     * @param param0.id The subscription id to get all resources under it
     * or subset of the resources if filter specified.
     * @param param0.tenantToken The tenant token to get all resources under
     * it or subset of the resources if filter specified.
     * @param param0.workspaceName The workspace name to get all info about this
     * workspace
     * @returns A promise with the azure resources.
     */
    const getAzureMLWorkspacesByName = async ({
        id,
        tenantToken,
        workspaceName,
        resourcegroups
    }: AzureServiceParams & { workspaceName: string; resourcegroups: string }) => {
        const urlOptions = getRequestOptions(tenantToken);

        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourcegroups')
            .resource(resourcegroups)
            .resource('providers', 'Microsoft.MachineLearningServices')
            .resource('workspaces')
            .resource(workspaceName)
            .queryParam('api-version', '2022-10-01-preview')
            .build();

        const { data } = await httpService.get<{ location: string }>(url, urlOptions);
        return data.location;
    };

    /**
     * Gets an Azure cognitive service accounts.
     * @docsLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/list
     *
     * @param param0.tenantToken The tenant token to use to get the accounts.
     * @param param0.path The LUIS account url
     * @returns A promise with the account.
     */
    const getAzureCsAccountByPath = async ({
        path,
        tenantToken
    }: Pick<AzureServiceParams, 'tenantToken'> & Pick<AzureCsAccountStore, 'path'>): Promise<AzureCsAccountSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);

        // Azure key paths are prefixed with a `/`.
        const url = `${azureBaseUrl}${path}`;

        const getUrl = urlBuilder()
            .baseUrl(url)
            .queryParam('api-version', '2021-10-01')
            .build();

        const { data: resource } = await httpService.get<AzureCsAccountSwagger>(getUrl, urlOptions);

        return resource;
    };

    /**
     * Updates an Azure cognitive service accounts.
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/update
     *
     * @param param0.tenantToken The tenant token to use to update the accounts.
     * @param param0.path The LUIS account url
     * @returns A promise with the account.
     */
    const updateAzureCsAccountByPath = async ({
        path,
        tenantToken,
        ...csAccountSwagger
    }: Pick<AzureServiceParams, 'tenantToken'> &
        Pick<AzureCsAccountStore, 'path'> &
        Partial<AzureCsAccountSwagger>): Promise<AzureCsAccountSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);

        // Azure key paths are prefixed with a `/`.
        const baseUrl = `${azureBaseUrl}${path}`;

        const url = urlBuilder()
            .baseUrl(baseUrl)
            .queryParam('api-version', '2021-10-01')
            .build();

        const { data: resource } = await httpService.patch<AzureCsAccountSwagger>(url, csAccountSwagger, urlOptions);

        return resource;
    };

    /**
     * Get the account keys for the specified Cognitive Services account.
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/list-keys
     *
     * @param param0.path The luis key url
     * @param param0.tenantToken The tenant token to use to get the keys.
     * @returns A promise with the key strings.
     */
    const getAzureCsAccountKeyStringsByPath = async ({
        path,
        tenantToken
    }: Pick<AzureServiceParams, 'tenantToken'> & Pick<AzureCsAccountStore, 'path'>): Promise<AzureCsAccountStore['keyStrings']> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = `${azureBaseUrl}${path}/listKeys`;
        const getUrl = urlBuilder()
            .baseUrl(url)
            .queryParam('api-version', '2021-10-01')
            .build();

        const { data: keyStrings } = await httpService.post<AzureCsAccountKeysSwagger>(getUrl, {}, urlOptions);

        return keyStrings;
    };

    /**
     * Create Azure Cognitive Service Account
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/accounts/create
     *
     * @param param0.tenantToken The tenant token to get all resources under
     * it or subset of the resources if filter specified.
     * @param param0.id The subscription id to get all resources  under it or
     * subset of the resources if filter specified.
     * @param param0.resourceGroupName The name of the resource group to create
     *  the account under
     * @param param0.keyName The name for the new key to be created
     * @param param0.keyProps : The http put request body required to
     * create the key
     * @returns A promise with the luis account.
     */
    const createAzureCsAccount = async ({
        id,
        keyName,
        keyProps,
        tenantToken,
        resourceGroupName
    }: AzureServiceParams & {
        keyName: string;
        resourceGroupName: string;
        keyProps: AzureCsAccountProps;
    }): Promise<AzureCsAccountSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', resourceGroupName)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('accounts', keyName)
            .queryParam('api-version', '2021-10-01')
            .build();

        const dto = {
            ...keyProps,
            sku: { name: keyProps.sku.code },
            properties: { ...keyProps.properties, customSubDomainName: keyName }
        };

        const { data: resource } = await httpService.put<AzureCsAccountSwagger>(url, dto, urlOptions);

        return resource;
    };

    /**
     * Loads the allowed skus list for the give azure subscription
     * @docLink https://docs.microsoft.com/en-us/rest/api/cognitiveservices/accountmanagement/resource-skus/list
     *
     * @param param0.armToken The tenant token.
     * @param param0.subscriptionId The subscription id of the given azure resource.
     */
    const getAllowedSkusBySubscription = async ({
        armToken,
        subscriptionId
    }: {
        armToken: string;
        subscriptionId: string;
    }): Promise<{ value: AzureSkuInfo[] }> => {
        const urlOptions = getRequestOptions(armToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', subscriptionId)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('skus')
            .queryParam('api-version', '2021-10-01')
            .build();

        const { data } = await httpService.get<{ value: AzureSkuInfo[] }>(url, urlOptions);

        return data;
    };

    /**
     * Creates or updates a resource group.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/resource-groups/create-or-update
     *
     * @param param0.id The subscription id to create resource group into.
     * @param param0.location The location to create the resource group in.
     * @param param0.tenantToken The tenant token to create resource group with.
     * @param param0.resourceGroupName The name of the resource group to create
     *  or update.
     * @returns A promise with the resource group.
     */
    const createResourceGroup = async ({
        id,
        location,
        tenantToken,
        resourceGroupName
    }: AzureServiceParams & {
        location: Region;
        resourceGroupName: string;
    }): Promise<AzureResourceGroupSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', resourceGroupName)
            .queryParam('api-version', '2021-04-01')
            .build();

        const { data: resourceGroup } = await httpService.put<AzureResourceGroupSwagger>(url, { location }, urlOptions);

        return resourceGroup;
    };

    /**
     * Get given resource group.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/resource-groups/get
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch resource group with.
     * @param param0.resourceGroupName The name of the resource group.
     * @returns A promise with the resource group.
     */
    const getAzureResourceGroup = async ({ id, tenantToken, resourceGroupName }: AzureServiceParams & { resourceGroupName: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', resourceGroupName)
            .queryParam('api-version', '2021-04-01')
            .build();

        const { data: resourceGroup } = await httpService.get<AzureResourceGroupSwagger>(url, urlOptions);

        return resourceGroup;
    };

    /**
     * Get all azure search services.
     * @docLink https://docs.microsoft.com/en-us/rest/api/searchmanagement/2021-04-01-preview/services/get
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @returns A promise with the azure search services.
     */
    const getAzureSearchResources = async ({ id, tenantToken }: AzureServiceParams) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.Search')
            .resource('searchServices')
            .queryParam('api-version', '2021-04-01-preview')
            .build();

        const { data: items } = await httpService.get<ResponseResourcesValue<AzureSearchServiceSwagger>>(url, urlOptions);

        return items.value;
    };

    /**
     * Create Azure Search Service Account
     * @docLink https://docs.microsoft.com/en-us/rest/api/searchmanagement/2021-04-01-preview/services/create-or-update
     *
     * @param param0.tenantToken The tenant token to get all services under
     * it or subset of the services if filter specified.
     * @param param0.id The subscription id to get all services  under it or
     * subset of the services if filter specified.
     * @param param0.resourceGroupName The name of the resource group to create
     *  the service under
     * @param param0.keyProps : The http put request body required to
     * create the service
     * @returns A promise with the search service.
     */
    const createAzureSearchResource = async ({
        id,
        keyProps,
        tenantToken,
        resourceGroupName
    }: AzureServiceParams & {
        resourceGroupName: string;
        keyProps: AzureSearchStore;
    }): Promise<AzureSearchServiceSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', resourceGroupName)
            .resource('providers', 'Microsoft.Search')
            .resource('searchServices', keyProps.name)
            .queryParam('api-version', '2021-04-01-preview')
            .build();

        const dto = { location: keyProps.location, sku: { name: keyProps.sku.name.toLowerCase() } };

        const { data: resource } = await httpService.put<AzureSearchServiceSwagger>(url, dto, urlOptions);

        return resource;
    };

    /**
     * Gets an Azure Search Service.
     * @docsLink https://docs.microsoft.com/en-us/rest/api/searchmanagement/2021-04-01-preview/services/get
     *
     * @param param0.tenantToken The tenant token to use to get the accounts.
     * @param param0.path The LUIS account url
     * @returns A promise with the account.
     */
    const getAzureSearchResourceByPath = async ({
        path,
        tenantToken
    }: Pick<AzureServiceParams, 'tenantToken'> & Pick<AzureSearchStore, 'path'>): Promise<AzureSearchServiceSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);

        // Azure key paths are prefixed with a `/`.
        const url = `${azureBaseUrl}${path}`;

        const getUrl = urlBuilder()
            .baseUrl(url)
            .queryParam('api-version', '2021-04-01-preview')
            .build();

        const { data: resource } = await httpService.get<AzureSearchServiceSwagger>(getUrl, urlOptions);

        return resource;
    };

    /**
     * Get Azure Search Service Admin Keys
     * @docLink https://docs.microsoft.com/en-us/rest/api/searchmanagement/2021-04-01-preview/admin-keys/get#adminkeyresult
     *
     * @param param0.tenantToken The tenant token to get all services under
     * it or subset of the services if filter specified.
     * @param param0.id The subscription id to get all services  under it or
     * subset of the services if filter specified.
     * @param param0.resourceGroupName The name of the resource group to create
     *  the service under.
     * @param param0.name : The name of the search service.
     * @returns A promise with the search service admin keys.
     */
    const getAzureSearchResourceAdminKeys = async ({
        id,
        name,
        tenantToken,
        resourceGroupName
    }: AzureServiceParams & {
        resourceGroupName: string;
        name: string;
    }): Promise<AzureSearchServiceAdminKeysSwagger> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourceGroups', resourceGroupName)
            .resource('providers', 'Microsoft.Search')
            .resource('searchServices', name)
            .resource('listAdminKeys')
            .queryParam('api-version', '2021-04-01-preview')
            .build();

        const { data: keys } = await httpService.post<AzureSearchServiceAdminKeysSwagger>(url, {}, urlOptions);

        return keys;
    };

    /**
     * Checks availability of azure search service name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/searchmanagement/2021-04-01-preview/services/check-name-availability
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.name the name of azure search service to check.
     * @returns A promise with the availability of azure search service name.
     */
    const checkAzureSearchResourceNameAvailability = async ({ id, tenantToken, name }: AzureServiceParams & { name: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.Search')
            .resource('checkNameAvailability')
            .queryParam('api-version', '2021-04-01-preview')
            .build();

        const { data: result } = await httpService.post<AzureSearchServiceNameAvailabilitySwagger>(
            url,
            { name, type: 'searchServices' },
            urlOptions
        );

        return result;
    };

    /**
     * Gets the Azure Cognitive Services resource provider info.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/providers/get
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @returns A promise indicating the registration is done.
     */
    const getAzureCsResourceProviderInfo = async ({ id, tenantToken }: AzureServiceParams) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.CognitiveServices')
            .queryParam('api-version', '2022-03-01-preview')
            .build();

        const { data: results } = await httpService.get<AzureCsResourceProviderInfoSwagger>(url, urlOptions);
        return results;
    };

    /**
     * Registers the Azure Cognitive Services resource provider.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/providers/register
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @returns A promise indicating the registration is done.
     */
    const registerAzureCsResourceProvider = async ({ id, tenantToken }: AzureServiceParams) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('register')
            .queryParam('api-version', '2022-03-01-preview')
            .build();

        const { data: results } = await httpService.post<AzureCsResourceProviderInfoSwagger>(url, {}, urlOptions);
        return results;
    };

    /**
     * Gets the Azure Cognitive services feature with the specified name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/features/get
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.featureName The Azure Cognitive Services feature name.
     * @returns A promise with feature information.
     */
    const getAzureCsFeature = async ({ id, tenantToken, featureName }: AzureServiceParams & { featureName: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.Features')
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('features', featureName)
            .queryParam('api-version', '2021-07-01')
            .build();

        const { data: results } = await httpService.get<AzureCsFeatureInfoSwagger>(url, urlOptions);

        return results;
    };

    /**
     * Registers the Azure Cognitive services feature with the specified name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/resources/features/get
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.featureName The Azure Cognitive Services feature name.
     * @returns A promise with registered feature information.
     */
    const registerAzureCsFeature = async ({ id, tenantToken, featureName }: AzureServiceParams & { featureName: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.Features')
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('features', featureName)
            .resource('register')
            .queryParam('api-version', '2021-07-01')
            .build();

        const { data: results } = await httpService.post<AzureCsFeatureInfoSwagger>(url, {}, urlOptions);

        return results;
    };

    /**
     * Lists permissions for an Azure Cognitive services with the specified name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/authorization/permissions/list-for-resource
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.resourceGroupName The Azure resource group name.
     * @param param0.accountName The Azure CS account name.
     * @returns A promise with a list of CS account permissions.
     */
    const listPermissionsForCsAccount = async ({
        id,
        nextLink,
        tenantToken,
        accountName,
        resourceGroupName
    }: AzureServiceParams & { resourceGroupName: string; accountName: string; nextLink?: string }): Promise<ResourceListResults<
        AzurePermissionsSwagger
    >> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url =
            nextLink ||
            urlBuilder()
                .baseUrl(azureBaseUrl)
                .resource('subscriptions', id)
                .resource('resourcegroups', resourceGroupName)
                .resource('providers', 'Microsoft.CognitiveServices')
                .resource('accounts', accountName)
                .resource('providers', 'Microsoft.Authorization')
                .resource('permissions')
                .queryParam('api-version', '2018-07-01')
                .build();

        const { data } = await httpService.get<ResourceListResults<AzurePermissionsSwagger>>(url, urlOptions);

        return data;
    };

    /**
     * Lists permissions for an Azure storage account with the specified name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/authorization/permissions/list-for-resource
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.resourceGroupName The Azure resource group name.
     * @param param0.accountName The Azure storage account name.
     * @returns A promise with a list of Azure storage account permissions.
     */
    const listPermissionsForAzureStorageAccount = async ({
        id,
        nextLink,
        tenantToken,
        accountName,
        resourceGroupName
    }: AzureServiceParams & { resourceGroupName: string; accountName: string; nextLink?: string }): Promise<ResourceListResults<
        AzurePermissionsSwagger
    >> => {
        const urlOptions = getRequestOptions(tenantToken);
        const url =
            nextLink ||
            urlBuilder()
                .baseUrl(azureBaseUrl)
                .resource('subscriptions', id)
                .resource('resourcegroups', resourceGroupName)
                .resource('providers', 'Microsoft.Storage')
                .resource('storageAccounts', accountName)
                .resource('providers', 'Microsoft.Authorization')
                .resource('permissions')
                .queryParam('api-version', '2018-07-01')
                .build();

        const { data } = await httpService.get<ResourceListResults<AzurePermissionsSwagger>>(url, urlOptions);

        return data;
    };

    /**
     * Lists permissions for an Azure storage account with the specified name.
     * @docLink https://docs.microsoft.com/en-us/rest/api/authorization/role-assignments/create-by-id
     *
     * @param param0.id The subscription id.
     * @param param0.tenantToken The tenant token to fetch the services with.
     * @param param0.resourceGroupName The Azure resource group name.
     * @param param0.accountName The Azure storage account name.
     * @param param0.principalId The principal id to add the role assignment for.
     * @param param0.roleDefinitionId The Azure RBAC role definition id.
     * @returns A promise indicating that role assignment adding is done.
     */
    const addRoleAssignmentForAzureStorageAccount = async ({
        id,
        tenantToken,
        resourceGroupName,
        accountName,
        principalId,
        roleDefinitionId
    }: AzureServiceParams & { resourceGroupName: string; accountName: string; principalId: string; roleDefinitionId: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourcegroups', resourceGroupName)
            .resource('providers', 'Microsoft.Storage')
            .resource('storageAccounts', accountName)
            .resource('providers', 'Microsoft.Authorization')
            .resource('roleAssignments', getGuid())
            .queryParam('api-version', '2018-07-01')
            .build();

        await httpService.put(
            url,
            {
                properties: {
                    principalId,
                    roleDefinitionId: `/subscriptions/${id}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionId}`
                }
            },
            urlOptions
        );
    };

    const addRoleAssignmentForAzureResource = async ({
        id,
        tenantToken,
        resourceGroupName,
        accountName,
        principalId,
        roleDefinitionId
    }: AzureServiceParams & { resourceGroupName: string; accountName: string; principalId: string; roleDefinitionId: string }) => {
        const urlOptions = getRequestOptions(tenantToken);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('resourcegroups', resourceGroupName)
            .resource('providers', 'Microsoft.CognitiveServices')
            .resource('accounts', accountName)
            .resource('providers', 'Microsoft.Authorization')
            .resource('roleAssignments', getGuid())
            .queryParam('api-version', '2018-07-01')
            .build();

        await httpService.put(
            url,
            {
                properties: {
                    principalId,
                    roleDefinitionId: `/subscriptions/${id}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionId}`
                }
            },
            urlOptions
        );
    };

    /**
     * Lists role assignments of azure resource.
     *
     * You can filter the roles by certain principal id (user or service principal)
     * If the user is a member of a group that has a role assignment, that role assignment is also listed.
     * If the user is a member of a group and that group is a member of another group
     * that has a role assignment, that role assignment is also listed.
     * This only accepts an object ID for a user or a service principal. You cannot pass an object ID for a group.
     *
     * @param param0.armToken The arm token.
     * @param param0.scope The scope of the role assignment to list.
     * @param param0.principalId Optional Id to filter the roles by.
     */
    const listAzureResourceRoleAssignments = async ({
        armToken,
        scope,
        principalId
    }: {
        armToken: string;
        scope: string;
        principalId?: string;
    }): Promise<RoleAssignmentSwagger> => {
        const urlOptions = getRequestOptions(armToken);
        const builder = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource(scope)
            .resource('providers', 'Microsoft.Authorization')
            .resource('roleAssignments')
            .queryParam('api-version', '2022-04-01');

        if (principalId) {
            builder.queryParam('$filter', `assignedTo('{${principalId}}')`);
        }

        const url = builder.build();

        const { data: resource } = await httpService.get<RoleAssignmentSwagger>(url, urlOptions);

        return resource;
    };

    return {
        getAzureTenants,
        getAzureCsFeature,
        createResourceGroup,
        createAzureCsAccount,
        getAzureResourceGroup,
        registerAzureCsFeature,
        getAzureCsAccountByPath,
        getAzureSearchResources,
        createAzureSearchResource,
        getAzureMLWorkspacesByName,
        updateAzureCsAccountByPath,
        listPermissionsForCsAccount,
        getAzureSearchResourceByPath,
        getAllowedSkusBySubscription,
        getAzureSubscriptionsByTenant,
        getAzureCsResourceProviderInfo,
        registerAzureCsResourceProvider,
        getAzureSearchResourceAdminKeys,
        listAzureResourceRoleAssignments,
        getAzureCsAccountKeyStringsByPath,
        addRoleAssignmentForAzureResource,
        getAzureCsAccountsBySubscriptionId,
        getAzureMLWorkspacesBySubscriptionId,
        getAzureCsAccountsByResourceGroupName,
        listPermissionsForAzureStorageAccount,
        getAzureResourceGroupsBySubscriptionId,
        addRoleAssignmentForAzureStorageAccount,
        checkAzureSearchResourceNameAvailability
    };
});
